import React, { useState, useEffect, useRef } from 'react';
import { FaSchool, FaEdit, FaKey, FaSave, FaTimes, FaCamera, FaMapMarkerAlt, FaPhone, FaEnvelope, FaGlobe } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * Component for school administrators to view and edit their school profile
 */
const SchoolProfile = () => {
  const { user, updateUserData } = useAuth();
  const { showToast } = useToast();
  const fileInputRef = useRef(null);
  
  // State for profile data
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    city: '',
    country: 'Kenya',
    postalCode: '',
    description: '',
    establishedYear: '',
    schoolType: '',
    curriculum: '',
    logo: ''
  });
  
  // State for password change
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Component state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [profileErrors, setProfileErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  
  // Fetch profile data on component mount
  useEffect(() => {
    fetchProfileData();
  }, []);
  
  // Fetch profile data from API
  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/schools/profile');
      
      // Set profile data
      setProfileData({
        name: response.data.data.name || '',
        email: response.data.data.email || '',
        phone: response.data.data.phone || '',
        website: response.data.data.website || '',
        address: response.data.data.address || '',
        city: response.data.data.city || '',
        country: response.data.data.country || 'Kenya',
        postalCode: response.data.data.postalCode || '',
        description: response.data.data.description || '',
        establishedYear: response.data.data.establishedYear || '',
        schoolType: response.data.data.schoolType || '',
        curriculum: response.data.data.curriculum || '',
        logo: response.data.data.logo || ''
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching profile data:', error);
      showToast('Failed to load school profile', 'error');
      setLoading(false);
    }
  };
  
  // Handle profile form field changes
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    
    setProfileData({
      ...profileData,
      [name]: value
    });
    
    // Clear error for this field
    setProfileErrors({
      ...profileErrors,
      [name]: undefined
    });
  };
  
  // Handle password form field changes
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    
    setPasswordData({
      ...passwordData,
      [name]: value
    });
    
    // Clear error for this field
    setPasswordErrors({
      ...passwordErrors,
      [name]: undefined
    });
  };
  
  // Handle logo change
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      showToast('Please upload a JPG or PNG image', 'error');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showToast('Image must be less than 5MB', 'error');
      return;
    }
    
    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(file);
    
    // Update form data with file
    setProfileData({
      ...profileData,
      logoFile: file
    });
  };
  
  // Handle logo click to open file dialog
  const handleLogoClick = () => {
    if (editMode) {
      fileInputRef.current.click();
    }
  };
  
  // Validate profile form
  const validateProfileForm = () => {
    const errors = {};
    
    if (!profileData.name.trim()) {
      errors.name = 'School name is required';
    }
    
    if (!profileData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(profileData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!profileData.phone.trim()) {
      errors.phone = 'Phone number is required';
    }
    
    if (profileData.website && !/^(http|https):\/\/[^ "]+$/.test(profileData.website)) {
      errors.website = 'Website URL is invalid';
    }
    
    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Validate password form
  const validatePasswordForm = () => {
    const errors = {};
    
    if (!passwordData.currentPassword) {
      errors.currentPassword = 'Current password is required';
    }
    
    if (!passwordData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters';
    }
    
    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Confirm password is required';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle profile form submission
  const handleProfileSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!validateProfileForm()) {
      return;
    }
    
    try {
      setSaving(true);
      
      const formData = new FormData();
      formData.append('name', profileData.name);
      formData.append('email', profileData.email);
      formData.append('phone', profileData.phone);
      formData.append('website', profileData.website);
      formData.append('address', profileData.address);
      formData.append('city', profileData.city);
      formData.append('country', profileData.country);
      formData.append('postalCode', profileData.postalCode);
      formData.append('description', profileData.description);
      formData.append('establishedYear', profileData.establishedYear);
      formData.append('schoolType', profileData.schoolType);
      formData.append('curriculum', profileData.curriculum);
      
      if (profileData.logoFile) {
        formData.append('logo', profileData.logoFile);
      }
      
      const response = await api.put('/schools/profile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      showToast('School profile updated successfully', 'success');
      setEditMode(false);
      setPreviewImage(null);
      
      // Refresh profile data
      fetchProfileData();
    } catch (error) {
      console.error('Error updating profile:', error);
      showToast('Failed to update school profile', 'error');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle password form submission
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) {
      return;
    }
    
    try {
      setChangingPassword(true);
      
      await api.put('/auth/updatepassword', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      showToast('Password updated successfully', 'success');
      
      // Reset password form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      showToast(error.response?.data?.error || 'Failed to change password', 'error');
    } finally {
      setChangingPassword(false);
    }
  };
  
  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditMode(false);
    setPreviewImage(null);
    fetchProfileData(); // Revert to saved data
  };
  
  // Get school type display name
  const getSchoolTypeDisplay = (type) => {
    const typeMap = {
      'primary': 'Primary School',
      'secondary': 'Secondary School',
      'combined': 'Combined (Primary & Secondary)',
      'college': 'College/University',
      'vocational': 'Vocational/Technical',
      'special': 'Special Education'
    };
    return typeMap[type] || type;
  };
  
  // Get curriculum display name
  const getCurriculumDisplay = (curriculum) => {
    const curriculumMap = {
      'cbc': 'Competency-Based Curriculum (CBC)',
      '8-4-4': '8-4-4 System',
      'igcse': 'International General Certificate of Secondary Education (IGCSE)',
      'ib': 'International Baccalaureate (IB)',
      'cambridge': 'Cambridge International',
      'montessori': 'Montessori',
      'other': 'Other'
    };
    return curriculumMap[curriculum] || curriculum;
  };
  
  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6 flex items-center">
        <FaSchool className="mr-2 text-primary" /> School Profile
      </h1>
      
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-6 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* School Logo Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-soft p-6">
              <div className="flex flex-col items-center">
                <div 
                  className={`relative w-48 h-48 overflow-hidden mb-4 ${editMode ? 'cursor-pointer' : ''}`}
                  onClick={handleLogoClick}
                >
                  {editMode && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <FaCamera className="text-white text-3xl" />
                    </div>
                  )}
                  <img 
                    src={previewImage || profileData.logo || 'https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/school-avatar_pe71l3.png'} 
                    alt={profileData.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoChange}
                  accept="image/jpeg, image/png"
                  className="hidden"
                />
                <h2 className="text-xl font-semibold mb-1 text-center">
                  {profileData.name}
                </h2>
                <p className="text-gray-600 mb-1 text-center">{profileData.email}</p>
                <p className="text-gray-600 mb-2 text-center">{profileData.phone}</p>
                
                {/* Edit/Cancel/Save Button */}
                {!editMode ? (
                  <button
                    type="button"
                    onClick={() => setEditMode(true)}
                    className="mt-3 flex items-center px-4 py-2 bg-primary border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-primary-dark"
                  >
                    <FaEdit className="mr-2" /> Edit Profile
                  </button>
                ) : (
                  <div className="flex space-x-3 mt-3">
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
                      disabled={saving}
                    >
                      <FaTimes className="mr-2" /> Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleProfileSubmit}
                      className="flex items-center px-4 py-2 bg-primary border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-primary-dark"
                      disabled={saving}
                    >
                      {saving ? <LoadingSpinner size="sm" className="mr-2" /> : <FaSave className="mr-2" />}
                      Save
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* School Details Card */}
            <div className="bg-white rounded-lg shadow-soft p-6 mt-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <FaGlobe className="mr-2 text-primary" /> School Details
              </h2>
              
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Type</p>
                  <p className="font-medium">{getSchoolTypeDisplay(profileData.schoolType) || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Curriculum</p>
                  <p className="font-medium">{getCurriculumDisplay(profileData.curriculum) || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Established Year</p>
                  <p className="font-medium">{profileData.establishedYear || 'Not specified'}</p>
                </div>
                {profileData.website && (
                  <div>
                    <p className="text-sm text-gray-500">Website</p>
                    <a 
                      href={profileData.website} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="font-medium text-primary hover:underline"
                    >
                      {profileData.website}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* School Information */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-soft p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <FaSchool className="mr-2 text-primary" /> School Information
              </h2>
              
              <form onSubmit={handleProfileSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  {/* School Name */}
                  <div className="sm:col-span-2">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      School Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={profileData.name}
                      onChange={handleProfileChange}
                      disabled={!editMode || saving}
                      className={`form-input w-full ${profileErrors.name ? 'border-red-500' : ''}`}
                    />
                    {profileErrors.name && (
                      <p className="text-red-500 text-xs mt-1">{profileErrors.name}</p>
                    )}
                  </div>
                  
                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={profileData.email}
                      onChange={handleProfileChange}
                      disabled={!editMode || saving}
                      className={`form-input w-full ${profileErrors.email ? 'border-red-500' : ''}`}
                    />
                    {profileErrors.email && (
                      <p className="text-red-500 text-xs mt-1">{profileErrors.email}</p>
                    )}
                  </div>
                  
                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={profileData.phone}
                      onChange={handleProfileChange}
                      disabled={!editMode || saving}
                      className={`form-input w-full ${profileErrors.phone ? 'border-red-500' : ''}`}
                    />
                    {profileErrors.phone && (
                      <p className="text-red-500 text-xs mt-1">{profileErrors.phone}</p>
                    )}
                  </div>
                  
                  {/* Website */}
                  <div>
                    <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                      Website
                    </label>
                    <input
                      type="url"
                      id="website"
                      name="website"
                      value={profileData.website}
                      onChange={handleProfileChange}
                      disabled={!editMode || saving}
                      placeholder="https://www.example.com"
                      className={`form-input w-full ${profileErrors.website ? 'border-red-500' : ''}`}
                    />
                    {profileErrors.website && (
                      <p className="text-red-500 text-xs mt-1">{profileErrors.website}</p>
                    )}
                  </div>
                  
                  {/* School Type */}
                  <div>
                    <label htmlFor="schoolType" className="block text-sm font-medium text-gray-700 mb-1">
                      School Type
                    </label>
                    <select
                      id="schoolType"
                      name="schoolType"
                      value={profileData.schoolType}
                      onChange={handleProfileChange}
                      disabled={!editMode || saving}
                      className="form-select w-full"
                    >
                      <option value="">Select School Type</option>
                      <option value="primary">Primary School</option>
                      <option value="secondary">Secondary School</option>
                      <option value="combined">Combined (Primary & Secondary)</option>
                      <option value="college">College/University</option>
                      <option value="vocational">Vocational/Technical</option>
                      <option value="special">Special Education</option>
                    </select>
                  </div>
                  
                  {/* Address */}
                  <div className="sm:col-span-2">
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={profileData.address}
                      onChange={handleProfileChange}
                      disabled={!editMode || saving}
                      className="form-input w-full"
                    />
                  </div>
                  
                  {/* City */}
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={profileData.city}
                      onChange={handleProfileChange}
                      disabled={!editMode || saving}
                      className="form-input w-full"
                    />
                  </div>
                  
                  {/* Country */}
                  <div>
                    <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                      Country
                    </label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      value={profileData.country}
                      onChange={handleProfileChange}
                      disabled={!editMode || saving}
                      className="form-input w-full"
                    />
                  </div>
                  
                  {/* Postal Code */}
                  <div>
                    <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      id="postalCode"
                      name="postalCode"
                      value={profileData.postalCode}
                      onChange={handleProfileChange}
                      disabled={!editMode || saving}
                      className="form-input w-full"
                    />
                  </div>
                  
                  {/* Established Year */}
                  <div>
                    <label htmlFor="establishedYear" className="block text-sm font-medium text-gray-700 mb-1">
                      Established Year
                    </label>
                    <input
                      type="number"
                      id="establishedYear"
                      name="establishedYear"
                      value={profileData.establishedYear}
                      onChange={handleProfileChange}
                      disabled={!editMode || saving}
                      min="1900"
                      max={new Date().getFullYear()}
                      className="form-input w-full"
                    />
                  </div>
                  
                  {/* Curriculum */}
                  <div>
                    <label htmlFor="curriculum" className="block text-sm font-medium text-gray-700 mb-1">
                      Curriculum
                    </label>
                    <select
                      id="curriculum"
                      name="curriculum"
                      value={profileData.curriculum}
                      onChange={handleProfileChange}
                      disabled={!editMode || saving}
                      className="form-select w-full"
                    >
                      <option value="">Select Curriculum</option>
                      <option value="cbc">Competency-Based Curriculum (CBC)</option>
                      <option value="8-4-4">8-4-4 System</option>
                      <option value="igcse">IGCSE</option>
                      <option value="ib">International Baccalaureate (IB)</option>
                      <option value="cambridge">Cambridge International</option>
                      <option value="montessori">Montessori</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  {/* Description */}
                  <div className="sm:col-span-2">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                      School Description
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={profileData.description}
                      onChange={handleProfileChange}
                      disabled={!editMode || saving}
                      rows="4"
                      className="form-textarea w-full"
                      placeholder="Provide a brief description of your school"
                    ></textarea>
                  </div>
                </div>
              </form>
            </div>
            
            {/* Password Change Form */}
            <div className="bg-white rounded-lg shadow-soft p-6 mt-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <FaKey className="mr-2 text-primary" /> Change Password
              </h2>
              
              <form onSubmit={handlePasswordSubmit}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Current Password */}
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Current Password
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      disabled={changingPassword}
                      className={`form-input w-full ${passwordErrors.currentPassword ? 'border-red-500' : ''}`}
                    />
                    {passwordErrors.currentPassword && (
                      <p className="text-red-500 text-xs mt-1">{passwordErrors.currentPassword}</p>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  {/* New Password */}
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      disabled={changingPassword}
                      className={`form-input w-full ${passwordErrors.newPassword ? 'border-red-500' : ''}`}
                    />
                    {passwordErrors.newPassword && (
                      <p className="text-red-500 text-xs mt-1">{passwordErrors.newPassword}</p>
                    )}
                  </div>
                  
                  {/* Confirm Password */}
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      disabled={changingPassword}
                      className={`form-input w-full ${passwordErrors.confirmPassword ? 'border-red-500' : ''}`}
                    />
                    {passwordErrors.confirmPassword && (
                      <p className="text-red-500 text-xs mt-1">{passwordErrors.confirmPassword}</p>
                    )}
                  </div>
                </div>
                
                <div className="mt-6">
                  <button
                    type="submit"
                    className="flex items-center px-4 py-2 bg-primary border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-primary-dark"
                    disabled={changingPassword}
                  >
                    {changingPassword ? <LoadingSpinner size="sm" className="mr-2" /> : <FaKey className="mr-2" />}
                    Change Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolProfile; 